<template>
  <div class="inventory-multi-groups-picker">
    <VsTimeline :last-item="lastElementOfVsTimeline">
      <VsTimelineItem
        v-for="(group, index) in modelValue"
        :key="group._uniqueId"
        :icon="groupIcon(group)"
      >
        <job-inventory-group-picker
          :ref="(el) => refCallback(el, index)"
          :model-value="group"
          :resource-type-activity-id="resourceTypeActivityId"
          :is-disabled="isDisabled || group.isDisabled"
          :allow-empty-pickup-location="!isLocationRequired"
          :allow-remove-inventory-group="
            modelValue.length > 1 && group.stopType === JobStopType.PickUp
          "
          :allow-add-inventory-item="
            group.stopType === JobStopType.PickUp && !isDisabled && !group.isDisabled
          "
          :show-add-inventory-group-button="
            !isDisabled &&
            ((group.stopType === JobStopType.PickUp && !group.isDisabled) ||
              !modelValue.some((g) => g.stopType === JobStopType.PickUp))
          "
          :has-pickup="modelValue.some((g) => g.stopType === JobStopType.PickUp)"
          @update:modelValue="updateGroup(index, $event)"
          @add-group="addGroup(index, group.stopType)"
          @remove-group="removeGroup(index)"
        />
      </VsTimelineItem>
    </VsTimeline>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import VsTimeline from '@/components/vision/VsTimeline.vue'
import VsTimelineItem from '@/components/vision/VsTimelineItem.vue'
import JobInventoryGroupPicker from './JobInventoryGroupPicker.vue'
import type { InventoryPickerGroup } from './types'
import { v4 } from 'uuid'
import { JobStopType } from '@ironsight/types'

interface InventoryMultiGroupsPickerProps {
  modelValue: InventoryPickerGroup[]
  resourceTypeActivityId: number
  isDisabled?: boolean
  isLocationRequired?: boolean
}

const props = withDefaults(defineProps<InventoryMultiGroupsPickerProps>(), {
  isDisabled: false,
  isLocationRequired: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: InventoryPickerGroup[]): void
}>()

const removeGroup = (index: number) => {
  const updatedGroups = props.modelValue.filter((_, i) => i !== index)
  emit('update:modelValue', updatedGroups)
}

const updateGroup = (index: number, updatedGroup: InventoryPickerGroup) => {
  const updatedGroups = [...props.modelValue]
  updatedGroups[index] = updatedGroup
  emit('update:modelValue', updatedGroups)
}

const addGroup = (index: number, stopType: JobStopType) => {
  const updatedGroups = [...props.modelValue]
  updatedGroups.splice(stopType === JobStopType.PickUp ? index + 1 : 0, 0, {
    _uniqueId: v4(),
    inventory: [{ _uniqueId: v4(), inventorySubTypeId: null, quantity: null, id: null }],
    stopType: JobStopType.PickUp,
    id: null,
  })
  emit('update:modelValue', updatedGroups)
}

const groupIcon = (group: InventoryPickerGroup) => {
  return group.stopType === JobStopType.PickUp ? 'mdi-map-marker-outline' : 'mdi-flag-outline'
}

const lastElementOfVsTimeline = ref()
const refCallback = (el, i) => {
  if (el && i === props.modelValue.length - 1) {
    lastElementOfVsTimeline.value = el
  }
}

defineExpose({ addGroup })
</script>

<style scoped>
.inventory-multi-groups-picker {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
